/**
 * TacticalContext Component
 * 
 * Displays context and objectives for chess tactical sequence challenges
 * with an expandable/collapsible section for detailed hints.
 */

import React, { useState } from 'react';
import styles from './TacticalContext.module.css';

/**
 * Props for the TacticalContext component
 */
export interface TacticalContextProps {
  /** Mission scenario/briefing description */
  scenarioDescription: string;
  /** Type of tactical objective */
  objectiveType: 'checkmate' | 'material' | 'position';
  /** Optional objective details */
  objectiveDetails?: string;
  /** Whether detailed view is expanded by default */
  isExpanded?: boolean;
  /** Optional handler for expand/collapse events */
  onToggleExpand?: () => void;
  /** Optional tactical hint to show in expanded view */
  tacticalHint?: string;
}

/**
 * Memoized TacticalContext Component
 * 
 * Displays mission context and objectives for chess tactical challenges
 * with an expandable details section that can show hints.
 */
const TacticalContextInternal: React.FC<TacticalContextProps> = ({
  scenarioDescription,
  objectiveType,
  objectiveDetails,
  isExpanded: initialExpanded = false,
  onToggleExpand,
  tacticalHint,
}) => {
  // Internal expanded state if no external control is provided
  const [internalExpanded, setInternalExpanded] = useState(initialExpanded);
  
  // Use either controlled or uncontrolled expand state
  const isExpanded = onToggleExpand ? initialExpanded : internalExpanded;
  
  // Handle toggling the expanded state
  const handleToggleExpand = () => {
    if (onToggleExpand) {
      onToggleExpand();
    } else {
      setInternalExpanded(!internalExpanded);
    }
  };
  
  /**
   * Render the objective type icon based on the objective type
   */
  const renderObjectiveIcon = () => {
    switch(objectiveType) {
      case 'checkmate':
        return (
          <div className={`${styles.objectiveIcon} ${styles.checkmateIcon}`} aria-hidden="true">
            ♚
          </div>
        );
      case 'material':
        return (
          <div className={`${styles.objectiveIcon} ${styles.materialIcon}`} aria-hidden="true">
            ♟
          </div>
        );
      case 'position':
        return (
          <div className={`${styles.objectiveIcon} ${styles.positionIcon}`} aria-hidden="true">
            ♞
          </div>
        );
      default:
        return null;
    }
  };

  /**
   * Get the objective type text
   */
  const getObjectiveTypeText = () => {
    switch(objectiveType) {
      case 'checkmate':
        return 'Checkmate Objective';
      case 'material':
        return 'Material Advantage';
      case 'position':
        return 'Positional Advantage';
      default:
        return 'Tactical Objective';
    }
  };

  return (
    <div 
      className={`${styles.container} ${isExpanded ? styles.expanded : ''}`}
      data-testid="tactical-context"
    >
      {/* Main context section (always visible) */}
      <div className={styles.mainContext}>
        <div className={styles.missionBriefing}>
          <h3 className={styles.briefingTitle}>Mission Briefing</h3>
          <p className={styles.briefingText}>{scenarioDescription}</p>
        </div>
        
        <div className={styles.objectiveSection}>
          {renderObjectiveIcon()}
          <div className={styles.objectiveContent}>
            <h3 className={styles.objectiveTitle}>{getObjectiveTypeText()}</h3>
            {objectiveDetails && (
              <p className={styles.objectiveDetails}>{objectiveDetails}</p>
            )}
          </div>
        </div>
        
        <button 
          className={styles.expandToggle}
          onClick={handleToggleExpand}
          aria-expanded={isExpanded}
          aria-controls="tactical-details"
          data-testid="expand-toggle"
        >
          <span className={styles.toggleText}>
            {isExpanded ? 'Hide Details' : 'Show Details'}
          </span>
          <span className={`${styles.toggleIcon} ${isExpanded ? styles.expanded : ''}`}>
            {isExpanded ? '▲' : '▼'}
          </span>
        </button>
      </div>
      
      {/* Expandable details section */}
      <div 
        id="tactical-details"
        className={`${styles.detailsSection} ${isExpanded ? styles.visible : ''}`}
        data-testid="tactical-details"
      >
        {tacticalHint ? (
          <div className={styles.tacticalHint}>
            <h4 className={styles.hintTitle}>Tactical Hint</h4>
            <p className={styles.hintText}>{tacticalHint}</p>
          </div>
        ) : (
          <div className={styles.noHint}>
            <p>Complete more of the sequence to unlock tactical hints.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(TacticalContextInternal); 